import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Keyboard, ScanLine } from 'lucide-react';
import type { CellCardDto } from '@eombor/contracts';
import { Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useBarcodeScanner } from '../../../shared/scanner/useBarcodeScanner.js';
import { useScannerClaim } from '../../../shared/scanner/scanner-claim.js';
import { useCellScan } from '../model/cell-scan.js';

export interface CellScanDialogProps {
  open: boolean;
  onClose: () => void;
  /** Skanerdan ochilgan bo'lsa — o'sha kod. Tugmadan ochilsa `null`. */
  initialCode?: string | null;
  /** Katak topildi — chaqiruvchi kartochkani ochadi (oyna marshrutni bilmaydi). */
  onOpenCell: (cell: CellCardDto) => void;
}

/**
 * SKANER OYNASI — qurilmaning ekrandagi aksi.
 *
 * Ikki yo'l ATAYLAB ajratilgan va bu ko'rinib turadi:
 *
 *   1. YUQORIDA — skaner. U yerda hech qanday maydon yo'q, chunki skanerda
 *      hech narsa yozilmaydi: odam qurilmani javonga qaratib bosadi, xolos.
 *      Ramka "o'qishga tayyor" ekanini ko'rsatadi va o'qilgan kod aynan
 *      o'sha yerda paydo bo'ladi — qayerga qarash kerakligi shubhasiz.
 *   2. PASTDA — qo'lda kiritish. Skaner ishlamay qolgan kun uchun zaxira
 *      yo'l, shuning uchun ikkinchi darajali ko'rinishda turadi.
 *
 * Ikkovini bitta maydonga qo'shib yuborish (avvalgi ko'rinish) ikkalasini ham
 * yomonlashtirardi: skaner uchun keraksiz maydon, qo'l uchun esa "bu maydon
 * skanerniki emasmi?" degan ikkilanish.
 *
 * Oyna ochiq turganda skanerni BAND QILADI — kod bu yerga tushishi kerak,
 * fondagi umumiy tinglovchiga emas.
 */
export const CellScanDialog = ({
  open,
  onClose,
  initialCode = null,
  onOpenCell,
}: CellScanDialogProps) => {
  const [manual, setManual] = useState('');
  /** Ramkada ko'rinadigan kod — skanerdan yoki qo'ldan, oxirgi urinish. */
  const [shown, setShown] = useState<string | null>(null);
  const manualRef = useRef<HTMLInputElement>(null);

  const cellScan = useCellScan((cell) => {
    onOpenCell(cell);
    onClose();
  });

  useScannerClaim(open);

  const scanner = useBarcodeScanner((code) => {
    if (!open) return;
    setShown(code);
    cellScan.scan(code);
  });

  /*
   * Skanerdan ochilgan oyna kodni DARHOL izlaydi: odam qurilmani bosgan va
   * ikkinchi marta bosishi kerak emas. `initialCode` o'zgargandagina
   * ishlaydi — qayta chizish so'rovni takrorlamaydi.
   */
  useEffect(() => {
    if (!open || initialCode === null) return;
    setShown(initialCode);
    cellScan.scan(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- faqat yangi kodga javob beradi
  }, [open, initialCode]);

  /* Yopilgach holat tozalanadi: keyingi ochilishda eski kod turmasin. */
  useEffect(() => {
    if (open) return;
    setManual('');
    setShown(null);
  }, [open]);

  const submitManual = (event: FormEvent): void => {
    event.preventDefault();
    setShown(manual.trim().toUpperCase());
    cellScan.scan(manual);
  };

  const choosing = cellScan.choices.length > 0;
  /* Topilmagan kod ramkada QIZIL bo'lib TURADI — xabar bir zumda yo'qolmasin. */
  const failed = cellScan.notFound !== null && !cellScan.pending;

  return (
    <Dialog open={open} onClose={onClose} title={t('warehouse.scanTitle')} size="md">
      <div className="flex flex-col gap-5">
        {/*
          O'QISH MAYDONI. Bu bezak emas — u uchta savolga javob beradi:
          qurilma tayyormi, nima o'qildi va u topildimi.
        */}
        <div
          className={cn(
            'relative overflow-hidden rounded-[var(--radius-lg)] border-2 bg-surface-2 px-4 py-8 transition-colors',
            failed ? 'border-danger' : choosing ? 'border-warn' : 'border-accent',
          )}
        >
          {/* Burchak qavslari — skaner nishonining o'zbek tilidagi tarjimasi. */}
          {['left-3 top-3 border-l-2 border-t-2', 'right-3 top-3 border-r-2 border-t-2',
            'left-3 bottom-3 border-b-2 border-l-2', 'right-3 bottom-3 border-b-2 border-r-2'].map(
            (corner) => (
              <span
                key={corner}
                aria-hidden
                className={cn('pointer-events-none absolute h-5 w-5 border-accent', corner)}
              />
            ),
          )}

          {/*
            Yuguruvchi nur — faqat KUTAYOTGANDA. Natija chiqqach u to'xtaydi:
            harakat diqqatni tortadi va u o'qilgan kodga tortilishi kerak.
            `motion-reduce` — harakatga sezgir odamlar uchun butunlay o'chadi.
          */}
          {shown === null && !cellScan.pending ? (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-6 top-0 h-px animate-[eombor-scan_2.2s_ease-in-out_infinite] bg-accent shadow-[0_0_12px_var(--color-accent)] motion-reduce:hidden"
            />
          ) : null}

          <div className="relative flex flex-col items-center gap-2 text-center">
            <span
              className={cn(
                'tabular text-2xl font-semibold tracking-[0.12em] sm:text-3xl',
                shown === null ? 'text-text-faint' : failed ? 'text-danger' : 'text-text',
              )}
            >
              {shown ?? '— — — — —'}
            </span>

            <span className="flex items-center gap-1.5 text-xs text-text-muted" aria-live="polite">
              <span
                aria-hidden
                className={cn(
                  'h-2 w-2 rounded-full',
                  scanner.detected ? 'bg-fill-high' : 'bg-border-strong',
                )}
              />
              {cellScan.pending
                ? t('warehouse.scanSearching')
                : failed
                  ? t('warehouse.scanNotFoundShort')
                  : choosing
                    ? t('warehouse.scanChooseHint')
                    : t(scanner.detected ? 'warehouse.scanReady' : 'warehouse.scanIdle')}
            </span>
          </div>
        </div>

        {/*
          Bir kod bir nechta omborda topildi — server TAXMIN QILMAYDI: noto'g'ri
          omborning katagi skladchini boshqa binoga yuborishi mumkin.
        */}
        {choosing ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs text-text-faint">{t('warehouse.scanChooseTitle')}</span>
            {cellScan.choices.map((cell) => (
              <button
                key={cell.id}
                type="button"
                onClick={() => {
                  cellScan.clearChoices();
                  onOpenCell(cell);
                  onClose();
                }}
                className="flex flex-col gap-0.5 rounded-[var(--radius-md)] border border-border p-3 text-left transition-colors hover:border-border-strong hover:bg-hover"
              >
                <span className="text-sm font-medium text-text">{cell.warehouseName}</span>
                <span className="text-xs text-text-faint">
                  {`${cell.zoneCode} · ${cell.zoneName}`}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {/* QO'LDA — zaxira yo'l, shuning uchun ajratuvchi chiziqdan keyin. */}
        <form onSubmit={submitManual} className="flex flex-col gap-3 border-t border-border pt-4">
          <span className="flex items-center gap-1.5 text-xs text-text-faint">
            <Keyboard size={14} aria-hidden />
            {t('warehouse.scanManualTitle')}
          </span>

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1 basis-48">
              <Input
                ref={manualRef}
                label={t('warehouse.scanLabel')}
                value={manual}
                onChange={(event) => setManual(event.target.value.toUpperCase())}
                placeholder="A-01-02-03"
                className="tabular"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <Button type="submit" loading={cellScan.pending} disabled={manual.trim() === ''}>
              <ScanLine size={16} aria-hidden />
              {t('warehouse.scanSubmit')}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};
